import { useLocalization } from '@assetron/common/contexts/localization/LocalizationContext';
import useSignupData from '@assetron/common/hooks/useSignupData';
import { Box, Typography } from '@mui/material';
import useIsMobile from '@assetron/web-common/hooks/useIsMobile';
import AssetronButton from '@assetron/web-common/components/common/AssetronButton';
import useSignupVerifyEmailStyles from './styles/useSignupVerifyEmailStyles';
import { EMPTY_STRING, OTP_LENGTH } from '@assetron/common/constants/constant';
import ErrorMessage from '@assetron/web-common/components/common/ErrorMessage';
import { createEnterKeyHandler } from '@assetron/common/utils/keyboardHandler';
import AuthAssetronInputField from '../common/AuthAssetronInputField';

interface SignupVerifyEmailProps {
  otp?: string;
  isInvalidOTP?: boolean;
  isResendDisabled?: boolean;
  onOtpChange?: (otp: string) => void;
  onContinueClick: () => void;
  onResendClick: () => void;
  isLoading: boolean;
}

function SignupVerifyEmail({
  otp = EMPTY_STRING,
  isInvalidOTP = false,
  isResendDisabled = false,
  onOtpChange = () => {},
  onContinueClick,
  onResendClick,
  isLoading,
}: Readonly<SignupVerifyEmailProps>) {
  const { t } = useLocalization();
  const isSmallScreen = useIsMobile();
  const {
    root,
    titleContainer,
    titleBox,
    textContainer,
    emailText,
    emailHighlight,
    mainContentContainer,
    mainContent,
    linkContainer,
    button,
  } = useSignupVerifyEmailStyles();

  const emailNotRecieved = t('verifyEmail.didNotReceiveEmail');
  const resendEmail = t('verifyEmail.resendEmail');
  const buttonText = t('verifyEmail.nextButton');
  const { userEmail } = useSignupData();

  return (
    <Box sx={root}>
      <Box sx={titleContainer}>
        <Box sx={titleBox}>
          <Typography
            variant={
              isSmallScreen
                ? 'heading_24_semibold'
                : 'large_heading_30_semibold'
            }
          >
            {t('verifyEmail.verifyEmailHeading')}
          </Typography>
        </Box>
        <Box sx={textContainer}>
          <Typography variant="body_14_regular" sx={emailText}>
            {t('verifyEmail.verifyEmailText1')}
            <Typography variant="body_14_regular" sx={emailHighlight}>
              {userEmail}
            </Typography>
            {t('verifyEmail.verifyEmailText2')}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={mainContentContainer}
        component="form"
        onKeyDown={createEnterKeyHandler({
          isEnabled: !isInvalidOTP && otp.length === OTP_LENGTH,
          isLoading,
          onEnter: onContinueClick,
        })}
      >
        <Box sx={textContainer}>
          <AuthAssetronInputField
            label={t('signInLandingPage.codeLabel')}
            value={otp}
            dataTestId="signup-otp-input"
            onTextChange={onOtpChange}
            destructive={isInvalidOTP}
            errorMessage={
              isInvalidOTP && <ErrorMessage message={t('InvalidOTP')} />
            }
          />
        </Box>
        <Box sx={mainContent}>
          <Box sx={linkContainer}>
            <Typography variant="body_14_regular">
              {emailNotRecieved}
            </Typography>
            <AssetronButton
              hierarchy="link-color"
              onClick={isResendDisabled ? undefined : onResendClick}
              disabled={isResendDisabled}
              dataTestId="signup-otp-resend-button"
            >
              <Typography variant="body_14_medium">{resendEmail}</Typography>
            </AssetronButton>
          </Box>
        </Box>
      </Box>
      <Box sx={button}>
        <AssetronButton
          size="md"
          onClick={onContinueClick}
          hierarchy="primary"
          disabled={isInvalidOTP || otp.length !== OTP_LENGTH}
          isLoading={isLoading}
          dataTestId="signup-otp-next-button"
        >
          <Typography variant="body_14_medium">{buttonText}</Typography>
        </AssetronButton>
      </Box>
    </Box>
  );
}

export default SignupVerifyEmail;
